import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import React, { useContext, useState } from "react";
import { NavLink } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { AuthContext } from "../contexts/AuthContext";
import { useLoadingToast } from "../hooks/toast/toasts";
import CompleteLogo from "../media/logo/CompleteLogoBlue.png";

export default function SignUpPage() {
  const { signUp } = useContext(AuthContext);
  const { initializeLoadingToast, updateLoadingToast } = useLoadingToast();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSignUpClick = async (event) => {
    event.preventDefault();
    let signUpToastId;

    try {
      setLoading(true);
      signUpToastId = initializeLoadingToast({
        text: "Criando sua conta...",
      });

      if (name === "" || email === "" || password === "") {
        const error = new Error("ClientError");
        error.response = {
          status: 400,
          statusText: "Preencha todos os campos",
        };
        throw error;
      }

      if (password.length < 6) {
        const error = new Error("ClientError");
        error.response = {
          status: 400,
          statusText: "A senha deve ter no mínimo 6 caracteres",
        };
        throw error;
      }

      await signUp(name, email, password);

      updateLoadingToast({
        id: signUpToastId,
        text: "Conta criada com sucesso!",
        type: "success",
      });
    } catch (error) {
      updateLoadingToast({
        id: signUpToastId,
        text:
          error?.response?.statusText ||
          error?.response?.data?.message ||
          "Verifique os campos e tente novamente.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      width="100vw"
      height="100vh"
      sx={{
        backgroundColor: "#1D1F2D",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          width: "600px",
          height: "600px",
          background: "radial-gradient(circle, rgba(202, 153, 0, 0.15) 0%, transparent 70%)",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        display="flex"
        width="90%"
        maxWidth="450px"
        sx={{
          borderRadius: 4,
          backgroundColor: "#ffffff",
          flexDirection: "column",
          alignItems: "center",
          p: 5,
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <img
          width="55%"
          style={{ marginBottom: 24 }}
          src={CompleteLogo}
          alt="Logo JBE Engenharia"
        />

        {/* Divisor */}
        <Box
          sx={{
            width: "60%",
            height: "1px",
            background: "linear-gradient(to right, transparent, #e5e7eb, transparent)",
            mb: 3,
          }}
        />

        <Typography
          variant="h5"
          sx={{
            color: "#1e293b",
            fontWeight: 700,
            mb: 0.5,
            textAlign: "center",
          }}
        >
          Criar nova conta
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#64748b",
            mb: 4,
            textAlign: "center",
          }}
        >
          Preencha os dados para se cadastrar
        </Typography>

        <ToastContainer />

        <form style={{ width: "100%" }} onSubmit={handleSignUpClick}>
          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "#374151",
              mb: 0.5,
            }}
          >
            Nome completo
          </Typography>
          <TextField
            fullWidth
            type="text"
            placeholder="Seu nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon sx={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2.5,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                borderRadius: 2,
                "& fieldset": {
                  borderColor: "#d1d5db",
                },
                "&:hover fieldset": {
                  borderColor: "#CA9900",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#CA9900",
                  borderWidth: 2,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 1.5,
              },
            }}
          />

          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "#374151",
              mb: 0.5,
            }}
          >
            E-mail
          </Typography>
          <TextField
            fullWidth
            type="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2.5,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                borderRadius: 2,
                "& fieldset": {
                  borderColor: "#d1d5db",
                },
                "&:hover fieldset": {
                  borderColor: "#CA9900",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#CA9900",
                  borderWidth: 2,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 1.5,
              },
            }}
          />

          <Typography
            component="label"
            sx={{
              display: "block",
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "#374151",
              mb: 0.5,
            }}
          >
            Senha
          </Typography>
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            placeholder="Mínimo 6 caracteres"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: "#9ca3af" }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
                borderRadius: 2,
                "& fieldset": {
                  borderColor: "#d1d5db",
                },
                "&:hover fieldset": {
                  borderColor: "#CA9900",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#CA9900",
                  borderWidth: 2,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 1.5,
              },
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#CA9900",
              color: "#fff",
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              boxShadow: "0 4px 14px rgba(202, 153, 0, 0.4)",
              "&:hover": {
                backgroundColor: "#b38800",
                boxShadow: "0 6px 20px rgba(202, 153, 0, 0.5)",
              },
              "&:disabled": {
                backgroundColor: "#e5e7eb",
                color: "#9ca3af",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: "#fff" }} />
            ) : (
              "Criar conta"
            )}
          </Button>
        </form>

        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: "1px solid #e5e7eb",
            width: "100%",
            textAlign: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Já possui conta?{" "}
            <NavLink
              to="/signIn"
              style={{
                textDecoration: "none",
                color: "#CA9900",
                fontWeight: 600,
              }}
            >
              Conecte-se
            </NavLink>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
